/**
 * Call Script Modal Component
 * Modal for creating and editing call scripts
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { CallScript } from '@/lib/types/call-center-settings';

interface CallScriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<CallScript>) => Promise<void>;
  script?: CallScript | null;
  companyId: string;
}

export function CallScriptModal({ isOpen, onClose, onSave, script, companyId }: CallScriptModalProps) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    script_type: 'greeting',
    content: '',
    is_active: true,
    display_order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (script) {
      setFormData({
        name: script.name,
        script_type: script.script_type,
        content: script.content,
        is_active: script.is_active,
        display_order: script.display_order
      });
    } else {
      setFormData({
        name: '',
        script_type: 'greeting',
        content: '',
        is_active: true,
        display_order: 0
      });
    }
  }, [script, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        company_id: companyId
      });
      onClose();
    } catch (error) {
      console.error('Error saving script:', error);
      alert('Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.colors.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
          {script ? 'Edit Call Script' : 'New Call Script'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Script Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="e.g., Inbound Sales Opening"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Script Type *
            </label>
            <select
              required
              value={formData.script_type}
              onChange={(e) => setFormData({ ...formData, script_type: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            >
              <option value="greeting">Greeting</option>
              <option value="inquiry">Inquiry</option>
              <option value="claim">Claim</option>
              <option value="closing">Closing</option>
              <option value="objection">Objection Handling</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Script Content *
            </label>
            <textarea
              required
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={8}
              className="w-full px-3 py-2 rounded border font-mono text-sm"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="Enter the call script content here..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Display Order
            </label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="w-32 px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="is_active" className="text-sm" style={{ color: theme.colors.textPrimary }}>
              Active (available for agents to use)
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.bgHover,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.primaryColor,
                color: '#ffffff'
              }}
            >
              {saving ? 'Saving...' : (script ? 'Update Script' : 'Create Script')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
